import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
  getBoolean,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyLinkBreakdown, SurveyLinkBreakdownDTO } from "./surveyLinkBreakdown";
import { SurveySubmissionData, SurveySubmissionDataDTO } from "./surveySubmissionData";
import { ProjectDetails, ProjectDetailsDTO } from "../types/projectDetails";
/* eslint-disable import/export */
export abstract class GetProjectSurveyResultsRPC implements
  LeoRPC<GetProjectSurveyResultsRPC.Request, GetProjectSurveyResultsRPC.Response, GetProjectSurveyResultsRPC.Errors.Errors>
{
  abstract execute(
    request: GetProjectSurveyResultsRPC.Request
  ): Promise<LeoRPCResult<GetProjectSurveyResultsRPC.Response, GetProjectSurveyResultsRPC.Errors.Errors>>;
}

export namespace GetProjectSurveyResultsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    export namespace SurveyResultsType {
      export interface AllSurveysDTO {
        case: string;
      }
      
      export class AllSurveys {
        readonly isSurveyResultsTypeAllSurveys: boolean = true;
      
      
      
      
      
        toDTO(): AllSurveysDTO {
          const dto: AllSurveysDTO = {
            case: "ALL_SURVEYS",
          };
          return dto;
        }
        }
      
      AllSurveys.prototype.toString = function toString(): string {
        return `SurveyResultsType.ALL_SURVEYS()`;
      };
      
      export interface SpecificSurveyDTO {
        case: string;
        surveyId: string;
      }
      
      export class SpecificSurvey {
        readonly isSurveyResultsTypeSpecificSurvey: boolean = true;
      
        readonly surveyId: LeoUUID;
      
      
        constructor(
        surveyId: LeoUUID
        ) {
          this.surveyId = surveyId;
        }
      
        static fromDTO(dto: SpecificSurveyDTO): SpecificSurvey {
          const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyResultsTypeSpecificSurveySurveyIdError);
          return new SpecificSurvey(
            surveyId
          );
        }
      
        toDTO(): SpecificSurveyDTO {
          const dto: SpecificSurveyDTO = {
            case: "SPECIFIC_SURVEY",
              surveyId: this.surveyId.toDTO()
          };
          return dto;
        }
        }
      
      SpecificSurvey.prototype.toString = function toString(): string {
        return `SurveyResultsType.SPECIFIC_SURVEY(surveyId='${this.surveyId}')`;
      };
      
    
      export type SurveyResultsType = AllSurveys | SpecificSurvey;
    
      export type SurveyResultsTypeDTO = AllSurveysDTO | SpecificSurveyDTO;
    
      export const fromDTO = (dto: SurveyResultsTypeDTO): SurveyResultsType => {
        switch (dto.case) {
          case "ALL_SURVEYS":
              return new AllSurveys();
          case "SPECIFIC_SURVEY":
              return SpecificSurvey.fromDTO(dto as SpecificSurveyDTO);
          default:
            throw new InvalidRequestSurveyResultsTypeError(
                `Case ${dto.case} is not valid case of SurveyResultsType.`
            );
        }
      };
    }
    
  }
  export interface RequestDTO {
    projectId: string;
    surveyResultsType: RequestEnums.SurveyResultsType.SurveyResultsTypeDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetProjectSurveyResultsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyResultsType: RequestEnums.SurveyResultsType.SurveyResultsType;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyResultsType: RequestEnums.SurveyResultsType.SurveyResultsType
    ) {
      this.projectId = projectId;
      this.surveyResultsType = surveyResultsType;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyResultsType = RequestEnums.SurveyResultsType.fromDTO(getObject(dto, "surveyResultsType", InvalidRequestSurveyResultsTypeError));
      return new Request(
        projectId,
        surveyResultsType
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyResultsType: this.surveyResultsType.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetProjectSurveyResultsRPC(projectId='${this.projectId}',surveyResultsType='${this.surveyResultsType}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  
  export namespace ResponseEnums {
    export namespace SurveyResults {
      export interface SurveyDataDTO {
        case: string;
        linkBreakdown: SurveyLinkBreakdownDTO;
        surveySubmissionData: SurveySubmissionDataDTO[];
        isDownloadingSurveyReportAllowed: boolean;
        isLinkTypeVisible: boolean;
      }
      
      export class SurveyData {
        readonly isSurveyResultsSurveyData: boolean = true;
      
        readonly linkBreakdown: SurveyLinkBreakdown;
        readonly surveySubmissionData: SurveySubmissionData[];
        readonly isDownloadingSurveyReportAllowed: boolean;
        readonly isLinkTypeVisible: boolean;
      
        static readonly constraints = {
          "surveySubmissionData": {
            "maxSize": 45,
          },
        }
      
        constructor(
        linkBreakdown: SurveyLinkBreakdown, 
        surveySubmissionData: SurveySubmissionData[], 
        isDownloadingSurveyReportAllowed: boolean, 
        isLinkTypeVisible: boolean
        ) {
          this.linkBreakdown = linkBreakdown;
          SurveyData.validateSurveySubmissionData(surveySubmissionData);
          this.surveySubmissionData = surveySubmissionData;
          this.isDownloadingSurveyReportAllowed = isDownloadingSurveyReportAllowed;
          this.isLinkTypeVisible = isLinkTypeVisible;
        }
      
        static fromDTO(dto: SurveyDataDTO): SurveyData {
          const linkBreakdown = SurveyLinkBreakdown.fromDTO(getObject(dto, "linkBreakdown", InvalidResponseSurveyResultsSurveyDataLinkBreakdownError));
          const surveySubmissionData = getList<SurveySubmissionDataDTO, SurveySubmissionData>(
            dto,
            "surveySubmissionData",
            SurveySubmissionData.fromDTO,
            InvalidResponseSurveyResultsSurveyDataSurveySubmissionDataError
          );
          const isDownloadingSurveyReportAllowed = getBoolean(dto, "isDownloadingSurveyReportAllowed", InvalidResponseSurveyResultsSurveyDataIsDownloadingSurveyReportAllowedError);
          const isLinkTypeVisible = getBoolean(dto, "isLinkTypeVisible", InvalidResponseSurveyResultsSurveyDataIsLinkTypeVisibleError);
          return new SurveyData(
            linkBreakdown,
            surveySubmissionData,
            isDownloadingSurveyReportAllowed,
            isLinkTypeVisible
          );
        }
      
        toDTO(): SurveyDataDTO {
          const dto: SurveyDataDTO = {
            case: "SURVEY_DATA",
              linkBreakdown: this.linkBreakdown.toDTO(),
              surveySubmissionData: this.surveySubmissionData.map((e: SurveySubmissionData) => { return e.toDTO() }),
              isDownloadingSurveyReportAllowed: this.isDownloadingSurveyReportAllowed,
              isLinkTypeVisible: this.isLinkTypeVisible
          };
          return dto;
        }
        private static validateSurveySubmissionData = (surveySubmissionData: SurveySubmissionData[]): void => {
          if(surveySubmissionData.length > 45) {
            throw new InvalidResponseSurveyResultsSurveyDataSurveySubmissionDataMaxSizeError(
              45,
              `Attribute surveySubmissionData is too long. Size is ${surveySubmissionData.length}. Max size is 45.`
            );
          }
        }
        
      }
      
      SurveyData.prototype.toString = function toString(): string {
        return `SurveyResults.SURVEY_DATA(linkBreakdown='${this.linkBreakdown}',surveySubmissionData='${this.surveySubmissionData}',isDownloadingSurveyReportAllowed='${this.isDownloadingSurveyReportAllowed}',isLinkTypeVisible='${this.isLinkTypeVisible}')`;
      };
      
      export interface NoSurveyDataDTO {
        case: string;
      }
      
      export class NoSurveyData {
        readonly isSurveyResultsNoSurveyData: boolean = true;
      
      
      
      
      
        toDTO(): NoSurveyDataDTO {
          const dto: NoSurveyDataDTO = {
            case: "NO_SURVEY_DATA",
          };
          return dto;
        }
        }
      
      NoSurveyData.prototype.toString = function toString(): string {
        return `SurveyResults.NO_SURVEY_DATA()`;
      };
      
    
      export type SurveyResults = SurveyData | NoSurveyData;
    
      export type SurveyResultsDTO = SurveyDataDTO | NoSurveyDataDTO;
    
      export const fromDTO = (dto: SurveyResultsDTO): SurveyResults => {
        switch (dto.case) {
          case "SURVEY_DATA":
              return SurveyData.fromDTO(dto as SurveyDataDTO);
          case "NO_SURVEY_DATA":
              return new NoSurveyData();
          default:
            throw new InvalidResponseSurveyResultsError(
                `Case ${dto.case} is not valid case of SurveyResults.`
            );
        }
      };
    }
    
  }
  export interface ResponseDTO {
    surveyResults: ResponseEnums.SurveyResults.SurveyResultsDTO;
    projectDetails: ProjectDetailsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetProjectSurveyResultsResponse: boolean = true;
  
    readonly surveyResults: ResponseEnums.SurveyResults.SurveyResults;
    readonly projectDetails: ProjectDetails;
  
  
    constructor(
    surveyResults: ResponseEnums.SurveyResults.SurveyResults, 
    projectDetails: ProjectDetails
    ) {
      this.surveyResults = surveyResults;
      this.projectDetails = projectDetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyResults = ResponseEnums.SurveyResults.fromDTO(getObject(dto, "surveyResults", InvalidResponseSurveyResultsError));
      const projectDetails = ProjectDetails.fromDTO(getObject(dto, "projectDetails", InvalidResponseProjectDetailsError));
      return new Response(
        surveyResults,
        projectDetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyResults: this.surveyResults.toDTO(),
          projectDetails: this.projectDetails.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetProjectSurveyResults(surveyResults='${this.surveyResults}',projectDetails='${this.projectDetails}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetProjectSurveyResultsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetProjectSurveyResultsRPC.INVALID_SURVEY_ID()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetProjectSurveyResultsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyResultsTypeError extends GetProjectSurveyResultsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyResultsTypeError";
  }
}

export class InvalidRequestSurveyResultsTypeAllSurveysError extends InvalidRequestSurveyResultsTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyResultsTypeAllSurveysError";
  }
}

export class InvalidRequestSurveyResultsTypeSpecificSurveyError extends InvalidRequestSurveyResultsTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyResultsTypeSpecificSurveyError";
  }
}

export class InvalidRequestSurveyResultsTypeSpecificSurveySurveyIdError extends InvalidRequestSurveyResultsTypeSpecificSurveyError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyResultsTypeSpecificSurveySurveyIdError";
  }
}

export class InvalidResponseSurveyResultsError extends GetProjectSurveyResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyResultsError";
  }
}

export class InvalidResponseSurveyResultsSurveyDataError extends InvalidResponseSurveyResultsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyResultsSurveyDataError";
  }
}

export class InvalidResponseSurveyResultsSurveyDataLinkBreakdownError extends InvalidResponseSurveyResultsSurveyDataError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyResultsSurveyDataLinkBreakdownError";
  }
}

export class InvalidResponseSurveyResultsSurveyDataSurveySubmissionDataError extends InvalidResponseSurveyResultsSurveyDataError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyResultsSurveyDataSurveySubmissionDataError";
  }
}

export class InvalidResponseSurveyResultsSurveyDataSurveySubmissionDataMaxSizeError extends InvalidResponseSurveyResultsSurveyDataSurveySubmissionDataError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyResultsSurveyDataSurveySubmissionDataMaxSizeError";
  }
}

export class InvalidResponseSurveyResultsSurveyDataIsDownloadingSurveyReportAllowedError extends InvalidResponseSurveyResultsSurveyDataError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyResultsSurveyDataIsDownloadingSurveyReportAllowedError";
  }
}

export class InvalidResponseSurveyResultsSurveyDataIsLinkTypeVisibleError extends InvalidResponseSurveyResultsSurveyDataError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyResultsSurveyDataIsLinkTypeVisibleError";
  }
}

export class InvalidResponseSurveyResultsNoSurveyDataError extends InvalidResponseSurveyResultsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyResultsNoSurveyDataError";
  }
}

export class InvalidResponseProjectDetailsError extends GetProjectSurveyResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectDetailsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetProjectSurveyResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetProjectSurveyResultsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

